import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { homeTranslations } from '../translations/home';
import { WhiteButton } from '../styles/SharedStyles';
import BlogPreview from '../components/BlogPreview';
import { getAllPosts } from '../utils/blogUtils';

// Styled components
const HomeContainer = styled.div`
  width: 100%;
  font-family: 'Montserrat', sans-serif;
`;

const HeroSection = styled.section`
  padding: 2rem 0 6rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  position: relative;
`;

const HeroBackground = styled.div`
  position: relative;
  width: 96%; /* Match footer width exactly */
  min-height: 70vh;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-image: url('/images/plix_home_background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  padding: 4rem 2rem;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 94%; /* Match footer mobile width exactly */
    min-height: 60vh;
  }
`;

const CloudOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
`;

const Cloud = styled.div`
  position: absolute;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0.32) 0%,
    rgba(0, 0, 0, 0.25) 30%,
    rgba(0, 0, 0, 0.15) 60%,
    transparent 100%
  );
  filter: blur(35px);
  border-radius: 50%;
  will-change: transform;
  
  @media (max-width: 768px) {
    background: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0.20) 0%,
      rgba(0, 0, 0, 0.15) 30%,
      rgba(0, 0, 0, 0.08) 60%,
      transparent 100%
    );
    filter: blur(25px);
  }
  
  &:nth-child(1) {
    width: 900px;
    height: 450px;
    top: -15%;
    animation: cloud-drift 35s linear infinite;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 650px;
    height: 400px;
    top: 15%;
    animation: cloud-drift 28s linear infinite;
    animation-delay: -10s;
  }
  
  &:nth-child(3) {
    width: 800px;
    height: 420px;
    top: 50%;
    animation: cloud-drift 32s linear infinite;
    animation-delay: -20s;
  }
  
  &:nth-child(4) {
    width: 600px;
    height: 350px;
    top: -10%;
    animation: cloud-drift 30s linear infinite;
    animation-delay: -15s;
  }
  
  &:nth-child(5) {
    width: 600px;
    height: 310px;
    top: 30%;
    animation: cloud-drift 34s linear infinite;
    animation-delay: -25s;
  }
  
  &:nth-child(7) {
    width: 620px;
    height: 340px;
    top: 85%;
    animation: cloud-drift 29s linear infinite;
    animation-delay: -8s;
  }
  
  @keyframes cloud-drift {
    0% { left: -50%; }
    100% { left: 150%; }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  display: grid;
  grid-template-rows: auto auto auto;
  grid-row-gap: 1.5rem;
  align-items: center;
  justify-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeroLogo = styled.img`
  max-width: 250px;
  width: 50%;
  height: auto;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    max-width: 200px;
  }
  
  @media (max-width: 480px) {
    max-width: 150px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.8rem;
  color: white;
  margin: 0;
  padding: 0;
  line-height: 1.3;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const Section = styled.section`
  padding: 6rem 2rem;
  background-color: white;
`;

const SectionContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Home = () => {
  const { language } = useLanguage();
  const homeT = homeTranslations[language];
  
  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSection id="home">
        <HeroBackground>
          <CloudOverlay>
            <Cloud />
            <Cloud />
            <Cloud />
            <Cloud />
            <Cloud />
            <Cloud />
          </CloudOverlay>
          <HeroContent>
            <HeroLogo src="/images/3000px_White.png" alt="Plix AI" />
            <HeroSubtitle>
              {homeT.heroSubtitle}
            </HeroSubtitle>
            <WhiteButton href="https://calendly.com/plix-ai-consulting/meeting" target="_blank" rel="noopener noreferrer">{homeT.heroButton}</WhiteButton>
          </HeroContent>
        </HeroBackground>
      </HeroSection>
      
      {/* Content Hub Section - Blog posts */}
      <Section id="latestposts">
        <SectionContent>
          <SectionTitle>{homeT.latestPosts || (language === 'de' ? 'Neueste Beiträge' : 'Latest Posts')}</SectionTitle>
          <BlogGrid>
            {getAllPosts().map((post) => (
              <BlogPreview key={post.id} post={post} />
            ))}
          </BlogGrid>
        </SectionContent>
      </Section>
    </HomeContainer>
  );
};

export default Home; 